// Point features on the map (flats, buildings, parcels without a stored outline)
// are small dots. An invisible, larger circle under the same features takes
// clicks, taps and the hover cursor, so a dot is easy to hit while it keeps its
// look. MapLibre queries a circle by its radius and stroke only; a transparent
// circle is not drawn but is still found by queryRenderedFeatures.

// 24 px radius for a mouse: a press about 0.5 cm (19 px) from a dot, or a
// little further, still opens it.
export const POINT_HIT_RADIUS_MOUSE = 24;
// 32 px radius for a finger on a touch screen.
export const POINT_HIT_RADIUS_TOUCH = 32;
// Dots closer to each other than this stand for one spot (several flats in
// one building share a point) and are offered together.
export const SAME_SPOT_DISTANCE_PX = 2;

export function isCoarsePointer(win) {
    try {
        return !!(win && typeof win.matchMedia === 'function' && win.matchMedia('(pointer: coarse)').matches);
    } catch (_error) {
        return false;
    }
}

export function pointHitRadius(coarsePointer) {
    return coarsePointer ? POINT_HIT_RADIUS_TOUCH : POINT_HIT_RADIUS_MOUSE;
}

/** Never smaller than the visible dot, so the dot itself always stays clickable. */
export function pointHitLayer(layerId, sourceId, radius, visibleRadius) {
    return {
        id: layerId,
        type: 'circle',
        source: sourceId,
        filter: ['==', '$type', 'Point'],
        paint: {
            'circle-radius': Math.max(Number(radius) || 0, Number(visibleRadius) || 0),
            'circle-color': '#000000',
            'circle-opacity': 0,
            'circle-stroke-width': 0,
        },
    };
}

/**
 * The enlarged hit circles of neighbouring dots overlap, so a press in a dense
 * block finds several dots. Only the dot nearest to the pointer is kept,
 * together with the dots on the same spot. An outline (a parcel or building
 * footprint) contains the pointer and is always kept; a dot next to it then
 * counts only when the pointer is on the visible dot itself.
 *
 * @param {Array} features rendered features under the pointer
 * @param {{x: number, y: number}} point pointer position on the map canvas
 * @param {function(Array): {x: number, y: number}} project lng/lat to canvas pixels
 * @param {number} visibleRadius radius of the visible dot in pixels
 */
export function nearestPointHits(features, point, project, visibleRadius) {
    const areas = [];
    const dots = [];
    (features || []).forEach((feature) => {
        const geometry = feature && feature.geometry;
        if (!geometry || geometry.type !== 'Point') {
            if (geometry) areas.push(feature);
            return;
        }
        const pixel = project(geometry.coordinates);
        const distance = pixel ? Math.hypot(pixel.x - point.x, pixel.y - point.y) : NaN;
        if (Number.isFinite(distance)) dots.push({ feature, pixel, distance });
    });
    if (!dots.length) return areas;
    const nearest = dots.reduce((best, dot) => (dot.distance < best.distance ? dot : best));
    if (areas.length && nearest.distance > (Number(visibleRadius) || 0)) return areas;
    return areas.concat(dots
        .filter(dot => Math.hypot(dot.pixel.x - nearest.pixel.x, dot.pixel.y - nearest.pixel.y) <= SAME_SPOT_DISTANCE_PX)
        .map(dot => dot.feature));
}
